export enum DeployStage {
  Test = 'test',
  Develop = 'develop',
  Staging = 'staging',
  Production = 'production',
}

export enum WebpackMode {
  Development = 'development',
  Production = 'production',
}

const DeployStageLevel: Readonly<Record<DeployStage, number>> = {
  [DeployStage.Test]: 0,
  [DeployStage.Develop]: 1,
  [DeployStage.Staging]: 2,
  [DeployStage.Production]: 3,
} as const

export interface EnvValues {
  webpackMode: WebpackMode
  deployStage: DeployStage
  apiUrl: string
  reactAppRecaptchaSiteKey: string
}

export class Env {
  static readonly shared = new Env()

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  private _webpackMode: EnvValues['webpackMode'] | undefined
  private _deployStage: EnvValues['deployStage'] | undefined
  private _apiUrl: EnvValues['apiUrl'] | undefined
  private _reactAppRecaptchaSiteKey: EnvValues['reactAppRecaptchaSiteKey'] | undefined

  get webpackMode(): EnvValues['webpackMode'] {
    if (this._webpackMode === undefined) {
      this._webpackMode = this.readWebpackModeFromProcess()
    }
    return this._webpackMode
  }

  get deployStage(): EnvValues['deployStage'] {
    if (this._deployStage === undefined) {
      this._deployStage = this.readDeployStageFromProcess()
    }
    return this._deployStage
  }

  get apiUrl(): EnvValues['apiUrl'] {
    if (this._apiUrl === undefined) {
      this._apiUrl = this.readApiUrlFromProcess()
    }
    return this._apiUrl
  }

  get reactAppRecaptchaSiteKey(): EnvValues['reactAppRecaptchaSiteKey'] {
    if (this._reactAppRecaptchaSiteKey === undefined) {
      this._reactAppRecaptchaSiteKey = this.readReactAppRecaptchaSiteKeyFromProcess()
    }
    return this._reactAppRecaptchaSiteKey
  }

  webPackModeIs(mode: WebpackMode): boolean {
    return this.webpackMode === mode
  }

  webPackModeIsNot(mode: WebpackMode): boolean {
    return this.webpackMode !== mode
  }

  webPackModeIsDevelopment(): boolean {
    return this.webpackMode === WebpackMode.Development
  }

  webPackModeIsProduction(): boolean {
    return this.webpackMode === WebpackMode.Production
  }

  stageIs(stage: DeployStage): boolean {
    return this.deployStage === stage
  }

  stageIsNot(stage: DeployStage): boolean {
    return this.deployStage !== stage
  }

  stageIsAbove(stage: DeployStage): boolean {
    return DeployStageLevel[this.deployStage] > DeployStageLevel[stage]
  }

  stageIsBelow(stage: DeployStage): boolean {
    return DeployStageLevel[this.deployStage] < DeployStageLevel[stage]
  }

  stageIsBetween(min: DeployStage, max: DeployStage): boolean {
    return (
      DeployStageLevel[this.deployStage] >= DeployStageLevel[min] &&
      DeployStageLevel[this.deployStage] <= DeployStageLevel[max]
    )
  }

  stageIsIn(...stages: DeployStage[]): boolean {
    return stages.some((stage) => this.stageIs(stage))
  }

  dump(sanitized = true): Readonly<EnvValues> {
    return {
      webpackMode: this.webpackMode,
      deployStage: this.deployStage,
      apiUrl: this.apiUrl,
      reactAppRecaptchaSiteKey: sanitized ? '[redacted]' : this.reactAppRecaptchaSiteKey,
    }
  }

  private readWebpackModeFromProcess(): EnvValues['webpackMode'] {
    if (!process.env.NODE_ENV) {
      throw new Error('NODE_ENV is not set.')
    }
    if (!Object.values(WebpackMode).includes(process.env.NODE_ENV as WebpackMode)) {
      throw new Error(`NODE_ENV is invalid: ${process.env.NODE_ENV}`)
    }
    return process.env.NODE_ENV as EnvValues['webpackMode']
  }

  private readDeployStageFromProcess(): EnvValues['deployStage'] {
    const deployStage = process.env.DEPLOY_STAGE
    if (!deployStage) {
      throw new Error('DEPLOY_STAGE is not set.')
    }
    if (!Object.values(DeployStage).includes(deployStage as DeployStage)) {
      throw new Error(`DEPLOY_STAGE is invalid: ${deployStage}`)
    }
    return deployStage as EnvValues['deployStage']
  }

  private readApiUrlFromProcess(): EnvValues['apiUrl'] {
    const apiUrl = process.env.API_URL
    if (!apiUrl) {
      throw new Error('API_URL is not set.')
    }
    // validate apiUrl is a valid url
    try {
      new URL(apiUrl)
    } catch (error) {
      throw new Error(`API_URL is invalid: ${apiUrl}`)
    }
    return apiUrl
  }

  private readReactAppRecaptchaSiteKeyFromProcess(): EnvValues['reactAppRecaptchaSiteKey'] {
    const reactAppRecaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY
    if (!reactAppRecaptchaSiteKey) {
      throw new Error('REACT_APP_RECAPTCHA_SITE_KEY is not set.')
    }
    return reactAppRecaptchaSiteKey
  }
}
